import React, { useState } from 'react';
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Calendar as CalendarIcon, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const COUNTRIES = [
  { code: "+94", name: "Sri Lanka", flag: "🇱🇰" },
  { code: "+91", name: "India", flag: "🇮🇳" },
  { code: "+1", name: "United States", flag: "🇺🇸" },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧" },
  { code: "+61", name: "Australia", flag: "🇦🇺" },
  { code: "+1", name: "Canada", flag: "🇨🇦" },
  { code: "+49", name: "Germany", flag: "🇩🇪" },
  { code: "+33", name: "France", flag: "🇫🇷" },
  { code: "+39", name: "Italy", flag: "🇮🇹" },
  { code: "+34", name: "Spain", flag: "🇪🇸" },
  { code: "+31", name: "Netherlands", flag: "🇳🇱" },
  { code: "+41", name: "Switzerland", flag: "🇨🇭" },
  { code: "+46", name: "Sweden", flag: "🇸🇪" },
  { code: "+47", name: "Norway", flag: "🇳🇴" },
  { code: "+45", name: "Denmark", flag: "🇩🇰" },
  { code: "+358", name: "Finland", flag: "🇫🇮" },
  { code: "+7", name: "Russia", flag: "🇷🇺" },
  { code: "+86", name: "China", flag: "🇨🇳" },
  { code: "+81", name: "Japan", flag: "🇯🇵" },
  { code: "+82", name: "South Korea", flag: "🇰🇷" },
  { code: "+65", name: "Singapore", flag: "🇸🇬" },
  { code: "+60", name: "Malaysia", flag: "🇲🇾" },
  { code: "+66", name: "Thailand", flag: "🇹🇭" },
  { code: "+62", name: "Indonesia", flag: "🇮🇩" },
  { code: "+63", name: "Philippines", flag: "🇵🇭" },
  { code: "+84", name: "Vietnam", flag: "🇻🇳" },
  { code: "+971", name: "UAE", flag: "🇦🇪" },
  { code: "+966", name: "Saudi Arabia", flag: "🇸🇦" },
  { code: "+974", name: "Qatar", flag: "🇶🇦" },
  { code: "+965", name: "Kuwait", flag: "🇰🇼" },
  { code: "+973", name: "Bahrain", flag: "🇧🇭" },
  { code: "+968", name: "Oman", flag: "🇴🇲" },
  { code: "+92", name: "Pakistan", flag: "🇵🇰" },
  { code: "+880", name: "Bangladesh", flag: "🇧🇩" },
  { code: "+977", name: "Nepal", flag: "🇳🇵" },
  { code: "+960", name: "Maldives", flag: "🇲🇻" },
  { code: "+27", name: "South Africa", flag: "🇿🇦" },
  { code: "+20", name: "Egypt", flag: "🇪🇬" },
  { code: "+90", name: "Turkey", flag: "🇹🇷" },
  { code: "+972", name: "Israel", flag: "🇮🇱" },
  { code: "+55", name: "Brazil", flag: "🇧🇷" },
  { code: "+52", name: "Mexico", flag: "🇲🇽" },
  { code: "+54", name: "Argentina", flag: "🇦🇷" },
  { code: "+64", name: "New Zealand", flag: "🇳🇿" },
  { code: "+353", name: "Ireland", flag: "🇮🇪" },
  { code: "+351", name: "Portugal", flag: "🇵🇹" },
  { code: "+30", name: "Greece", flag: "🇬🇷" },
  { code: "+48", name: "Poland", flag: "🇵🇱" },
  { code: "+420", name: "Czech Republic", flag: "🇨🇿" },
  { code: "+43", name: "Austria", flag: "🇦🇹" },
  { code: "+32", name: "Belgium", flag: "🇧🇪" },
];

export default function Booking() {
  const settings = useSiteSettings();
  const [searchParams] = useSearchParams();
  const presetPlace = searchParams.get("place") || "";
  const presetInterest = searchParams.get("interest") || "";

  const [date, setDate] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [countryCode, setCountryCode] = useState("+94");
  const [country, setCountry] = useState("Sri Lanka");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Gather form data
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    
    // Construct email body
    const subject = "New Booking Request - CITIZEN TRAVELS";
    const body = `
New Booking Request Details:

Name: ${data.firstName} ${data.lastName}
Email: ${data.email}
Country: ${country}
Phone: ${countryCode} ${data.phone}
Travel Date: ${date ? format(date, "PPP") : "Not selected"}
Duration: ${data.duration || "Not specified"}
Interest: ${data.interest || "Not specified"}

Special Requests:
${data.message}
    `;

    // Persist booking to backend (best-effort; works for guests and logged-in users)
    try {
      await api.post("/bookings", {
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        country,
        country_code: countryCode,
        phone: data.phone,
        travel_date: date ? format(date, "PPP") : null,
        duration: data.duration || null,
        interest: data.interest || null,
        message: data.message || null,
      });
    } catch (err) {
      // non-blocking: booking still proceeds via mailto
      console.warn("Booking save failed", err);
    }

    // Open mail client
    window.location.href = `mailto:${settings.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // Show success state
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      toast.success("Opening your email client to send the request!");
    }, 1000);
  };

  const handleWhatsApp = () => {
    const message = "Hi! I'm interested in planning a trip to Sri Lanka.";
    window.open(`https://wa.me/${settings.whatsapp}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background font-sans flex flex-col">
        <Navbar />
        <main className="flex-grow flex items-center justify-center p-4 pt-24">
          <Card className="w-full max-w-lg text-center p-8 border-primary/10 shadow-2xl">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600" />
              </div>
            </div>
            <h2 className="text-3xl font-serif font-bold text-primary mb-4">Request Received!</h2>
            <p className="text-muted-foreground mb-8 text-lg">
              Thank you for choosing CITIZEN TRAVELS. We have opened your email client to send the request to <strong>nextgenlanka67@gmail.com</strong>.
            </p>
            <Button 
              className="w-full bg-primary text-white hover:bg-primary/90 rounded-full"
              onClick={() => window.location.href = '/'}
            >
              Return Home
            </Button>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-sans flex flex-col">
      <Navbar />
      
      <main className="flex-grow pt-24 pb-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            
            {/* Left Side - Content */}
            <div className="space-y-8 lg:sticky lg:top-28">
              <div>
                <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary mb-4">
                  Start Your <span className="text-secondary">Journey</span>
                </h1>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Ready to explore the wonders of Sri Lanka? Fill out the form, and let us craft a travel experience tailored just for you.
                </p>
              </div>

              <div className="grid gap-6">
                {[
                  { title: "Personalized Itineraries", desc: "Trips designed around your interests and pace." },
                  { title: "Expert Local Guides", desc: "Discover hidden gems with our knowledgeable guides." },
                  { title: "24/7 Support", desc: "Travel with peace of mind knowing we're always here." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 p-4 rounded-xl bg-white border border-primary/5 shadow-sm">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <h3 className="font-serif font-bold text-lg text-primary">{item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side - Form */}
            <Card className="border-primary/10 shadow-xl bg-white/50 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="font-serif text-2xl text-primary">Trip Details</CardTitle>
                <CardDescription>Tell us about your travel plans</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input id="firstName" name="firstName" placeholder="John" required className="bg-white" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input id="lastName" name="lastName" placeholder="Doe" required className="bg-white" />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" name="email" type="email" placeholder="john@example.com" required className="bg-white" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <div className="flex gap-2">
                      <Select
                        value={`${country}|${countryCode}`}
                        onValueChange={(val) => {
                          const [n, c] = val.split("|");
                          setCountry(n);
                          setCountryCode(c);
                        }}
                      >
                        <SelectTrigger
                          data-testid="country-code-select"
                          className="bg-white w-[140px] shrink-0"
                        >
                          <SelectValue placeholder="Country" />
                        </SelectTrigger>
                        <SelectContent className="max-h-72">
                          {COUNTRIES.map((c) => (
                            <SelectItem
                              key={`${c.name}-${c.code}`}
                              value={`${c.name}|${c.code}`}
                              data-testid={`country-option-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                            >
                              <span className="mr-2">{c.flag}</span>
                              {c.name} ({c.code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="7X XXX XXXX"
                        className="bg-white flex-1"
                        data-testid="phone-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Travel Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full justify-start text-left font-normal bg-white",
                              !date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {date ? format(date, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={date}
                            onSelect={setDate}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="space-y-2">
                      <Label>Duration</Label>
                      <Select name="duration">
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Select duration" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3-5 Days">3-5 Days</SelectItem>
                          <SelectItem value="5-7 Days">5-7 Days</SelectItem>
                          <SelectItem value="7-10 Days">7-10 Days</SelectItem>
                          <SelectItem value="10-14 Days">10-14 Days</SelectItem>
                          <SelectItem value="2 Weeks+">2 Weeks+</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Interested Destinations</Label>
                    <Select
                      name="interest"
                      defaultValue={
                        ["Heritage", "Culture"].includes(presetInterest)
                          ? "Culture & Heritage"
                          : ["Wildlife", "Nature"].includes(presetInterest)
                          ? "Nature & Wildlife"
                          : presetInterest === "Beach"
                          ? "Beaches"
                          : undefined
                      }
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Select main interest" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Culture & Heritage">Culture & Heritage (Sigiriya, Kandy)</SelectItem>
                        <SelectItem value="Nature & Wildlife">Nature & Wildlife (Yala, Ella)</SelectItem>
                        <SelectItem value="Beaches">Beaches (Mirissa, Galle)</SelectItem>
                        <SelectItem value="All">All of the above</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Special Requests</Label>
                    <Textarea
                      id="message"
                      name="message"
                      defaultValue={presetPlace ? `I'm interested in visiting ${presetPlace}.` : ""}
                      placeholder="Tell us about your preferences, dietary requirements, or specific places you want to visit..."
                      className="bg-white min-h-[100px]"
                    />
                  </div>

                  <div className="space-y-3">
                    <Button 
                      type="submit" 
                      className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 text-lg font-medium"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Opening Email..." : "Send Booking Request"}
                    </Button>
                    
                    <div className="relative flex items-center py-2">
                      <div className="flex-grow border-t border-gray-200"></div>
                      <span className="flex-shrink-0 mx-4 text-gray-400 text-sm">Or</span>
                      <div className="flex-grow border-t border-gray-200"></div>
                    </div>

                    <Button 
                      type="button"
                      variant="outline"
                      className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366] hover:text-white rounded-full h-12 text-lg font-medium transition-colors"
                      onClick={handleWhatsApp}
                    >
                      <svg viewBox="0 0 24 24" className="w-5 h-5 mr-2 fill-current" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                      </svg>
                      Chat on WhatsApp
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
